import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiBookOpen,
    FiCalendar,
    FiClock,
    FiLayers,
    FiFilter,
} from "react-icons/fi";
import portalService from "../../../services/portalService";

const extract = (r) => r?.data ?? r;
const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const STATUS_COLORS = {
    Present: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    Late: { bg: "#fef9c3", color: "#92400e", border: "#fde047" },
    "Half Day": { bg: "#fef9c3", color: "#92400e", border: "#fde047" },
    Absent: { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5" },
    "On Leave": { bg: "#e8f1f8", color: "#002747", border: "#93c5fd" },
    "Medical Leave": { bg: "#e8f1f8", color: "#002747", border: "#93c5fd" },
    Unmarked: { bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" },
};

const now = new Date();

const formatDisplayDate = (value = "") => {
    if (!value) return "—";
    const parts = value.split("-");
    if (parts.length !== 3) return value;
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
};

export default function StudentAttendance() {
    const [activeTab, setActiveTab] = useState("subject"); // "subject" | "daily"

    // Time filter states
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(now.getFullYear());
    const yearOpts = Array.from({ length: 3 }, (_, i) => now.getFullYear() - i);

    // Daily Attendance state
    const [dailyData, setDailyData] = useState(null);
    const [dailyLoading, setDailyLoading] = useState(true);

    // Subject Attendance state
    const [subjectData, setSubjectData] = useState(null);
    const [subjectLoading, setSubjectLoading] = useState(true);
    const [selectedSubjectFilter, setSelectedSubjectFilter] = useState("");

    // Fetch Daily Attendance
    const fetchDailyData = useCallback(() => {
        setDailyLoading(true);
        const m = String(month).padStart(2, "0");
        const lastD = new Date(year, month, 0).getDate();
        portalService
            .getAttendance({
                fromDate: `${year}-${m}-01`,
                toDate: `${year}-${m}-${String(lastD).padStart(2, "0")}`,
            })
            .then((r) => setDailyData(extract(r)))
            .catch((err) => toast.error(err.response?.data?.message || err.message))
            .finally(() => setDailyLoading(false));
    }, [month, year]);

    // Fetch Subject Attendance
    const fetchSubjectData = useCallback(() => {
        setSubjectLoading(true);
        const m = String(month).padStart(2, "0");
        const lastD = new Date(year, month, 0).getDate();
        portalService
            .getSubjectAttendance({
                fromDate: `${year}-${m}-01`,
                toDate: `${year}-${m}-${String(lastD).padStart(2, "0")}`,
                month,
                year,
                subjectId: selectedSubjectFilter || undefined,
            })
            .then((r) => setSubjectData(extract(r)))
            .catch((err) => toast.error(err.response?.data?.message || err.message))
            .finally(() => setSubjectLoading(false));
    }, [month, year, selectedSubjectFilter]);

    useEffect(() => {
        if (activeTab === "daily") {
            fetchDailyData();
        } else {
            fetchSubjectData();
        }
    }, [activeTab, fetchDailyData, fetchSubjectData]);

    // Daily Attendance Computations
    const dailyEntries = dailyData?.entries || [];
    const dailySummary = dailyData?.summary || {};
    const dailyPct = dailySummary.percentage ?? 0;
    const dailyColor = dailyPct >= 75 ? "#16a34a" : dailyPct >= 60 ? "#d97706" : "#dc2626";

    const dateMap = {};
    dailyEntries.forEach((e) => {
        dateMap[e.date] = e;
    });

    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = Array(firstDay)
        .fill(null)
        .concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));
    const todayStr = now.toISOString().slice(0, 10);

    // Subject Attendance Computations
    const overall = subjectData?.overall || {};
    const bySubject = subjectData?.bySubject || [];
    const subjectEntries = subjectData?.entries || [];
    const subjectPct = overall.percentage ?? 0;
    const subjectColor = subjectPct >= 75 ? "#16a34a" : subjectPct >= 60 ? "#d97706" : "#dc2626";

    return (
        <div style={{ maxWidth: 1050, margin: "0 auto" }}>
            {/* ── Top Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem", marginBottom: "1rem" }}>
                <div>
                    <h1 className="page-title">My Attendance</h1>
                    <p className="page-subtitle">
                        {MONTHS_LONG[month]} {year} · Track your lecture and daily roll call attendance
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <select
                        className="form-control"
                        style={{ width: 125, fontSize: 13 }}
                        value={month}
                        onChange={(e) => setMonth(Number(e.target.value))}
                    >
                        {MONTHS.slice(1).map((m, i) => (
                            <option key={i + 1} value={i + 1}>
                                {m}
                            </option>
                        ))}
                    </select>
                    <select
                        className="form-control"
                        style={{ width: 95, fontSize: 13 }}
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                    >
                        {yearOpts.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* ── Navigation Tabs ── */}
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--border)", paddingBottom: "0.6rem" }}>
                <button
                    type="button"
                    className={`btn ${activeTab === "subject" ? "btn-primary" : "btn-outline"}`}
                    onClick={() => setActiveTab("subject")}
                    style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: "0.4rem", borderRadius: 8 }}
                >
                    <FiBookOpen size={14} /> Subject-wise Attendance
                </button>
                <button
                    type="button"
                    className={`btn ${activeTab === "daily" ? "btn-primary" : "btn-outline"}`}
                    onClick={() => setActiveTab("daily")}
                    style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: "0.4rem", borderRadius: 8 }}
                >
                    <FiCalendar size={14} /> Daily Roll Call
                </button>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════
                TAB 1: SUBJECT-WISE ATTENDANCE
               ══════════════════════════════════════════════════════════════════════ */}
            {activeTab === "subject" && (
                <div>
                    {/* Status Alerts */}
                    {subjectPct > 0 && subjectPct < 75 && (
                        <div
                            style={{
                                background: "#fef2f2",
                                border: "1px solid #fecaca",
                                borderRadius: 8,
                                padding: "0.75rem 1rem",
                                marginBottom: "1rem",
                                fontSize: 13,
                                color: "#991b1b",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <FiAlertTriangle size={16} />
                            <span>
                                Your overall subject attendance is <strong>{subjectPct}%</strong> — below the required <strong>75%</strong> criteria for exams.
                            </span>
                        </div>
                    )}
                    {subjectPct >= 75 && (
                        <div
                            style={{
                                background: "#f0fdf4",
                                border: "1px solid #bbf7d0",
                                borderRadius: 8,
                                padding: "0.75rem 1rem",
                                marginBottom: "1rem",
                                fontSize: 13,
                                color: "#15803d",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <FiCheckCircle size={16} />
                            <span>
                                Great job! Your subject attendance is <strong>{subjectPct}%</strong> (meeting college criteria).
                            </span>
                        </div>
                    )}

                    {subjectLoading ? (
                        <div className="loading-center">
                            <div className="spinner" /> Loading subject attendance…
                        </div>
                    ) : (
                        <div>
                            {/* Summary + Subject Cards */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 260px", gap: "1rem", alignItems: "start", marginBottom: "1.25rem" }}>
                                {/* Subject Cards Grid */}
                                <div className="card">
                                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontWeight: 700 }}>
                                            <FiLayers size={14} /> Subject Breakdown ({bySubject.length})
                                        </span>
                                        {bySubject.length > 0 && (
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                                <FiFilter size={12} color="var(--text-muted)" />
                                                <select
                                                    className="form-control"
                                                    style={{ width: 140, fontSize: 12, padding: "2px 6px" }}
                                                    value={selectedSubjectFilter}
                                                    onChange={(e) => setSelectedSubjectFilter(e.target.value)}
                                                >
                                                    <option value="">All Subjects</option>
                                                    {bySubject.map((s) => (
                                                        <option key={s.subjectId} value={s.subjectId}>
                                                            {s.subjectName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>

                                    {bySubject.length === 0 ? (
                                        <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                                            No subjects or lecture records found for this period.
                                        </div>
                                    ) : (
                                        <div style={{ padding: "1rem", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0.85rem" }}>
                                            {bySubject.map((s) => {
                                                const sPct = s.percentage;
                                                const sColor = sPct >= 75 ? "#16a34a" : sPct >= 60 ? "#d97706" : "#dc2626";
                                                return (
                                                    <div
                                                        key={s.subjectId}
                                                        style={{
                                                            border: `1px solid ${s.isLowAttendance ? "#fca5a5" : "var(--border)"}`,
                                                            background: s.isLowAttendance ? "#fff5f5" : "#fafafa",
                                                            borderRadius: 10,
                                                            padding: "0.85rem 1rem",
                                                        }}
                                                    >
                                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                                                            <div>
                                                                <div style={{ fontWeight: 700, fontSize: 13.5, color: "var(--text)" }}>
                                                                    {s.subjectName}
                                                                </div>
                                                                <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>
                                                                    {s.code ? `${s.code} · ` : ""}{s.type}
                                                                </div>
                                                            </div>
                                                            <span
                                                                style={{
                                                                    fontSize: 14,
                                                                    fontWeight: 800,
                                                                    color: sColor,
                                                                    background: "#fff",
                                                                    padding: "2px 8px",
                                                                    borderRadius: 99,
                                                                    border: `1px solid ${sColor}40`,
                                                                }}
                                                            >
                                                                {sPct}%
                                                            </span>
                                                        </div>

                                                        {/* Progress bar */}
                                                        <div style={{ background: "#e2e8f0", height: 6, borderRadius: 99, overflow: "hidden", margin: "8px 0" }}>
                                                            <div style={{ width: `${Math.min(sPct, 100)}%`, background: sColor, height: "100%" }} />
                                                        </div>

                                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "var(--text-muted)" }}>
                                                            <span>Attended: <strong style={{ color: "#16a34a" }}>{s.attended}</strong> / {s.totalLectures}</span>
                                                            <span>Absent: <strong style={{ color: "#dc2626" }}>{s.absent}</strong></span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Overall KPI summary widget */}
                                <div className="card" style={{ padding: "1.25rem", textAlign: "center" }}>
                                    <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                                        Monthly Lecture Overall
                                    </div>
                                    <svg width={110} height={110} style={{ display: "block", margin: "0 auto 0.75rem" }}>
                                        <circle cx={55} cy={55} r={44} fill="none" stroke={`${subjectColor}18`} strokeWidth={10} />
                                        <circle
                                            cx={55}
                                            cy={55}
                                            r={44}
                                            fill="none"
                                            stroke={subjectColor}
                                            strokeWidth={10}
                                            strokeDasharray={`${2 * Math.PI * 44}`}
                                            strokeDashoffset={`${2 * Math.PI * 44 * (1 - subjectPct / 100)}`}
                                            strokeLinecap="round"
                                            transform="rotate(-90 55 55)"
                                        />
                                        <text x={55} y={50} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 20, fontWeight: 900, fill: subjectColor }}>
                                            {subjectPct}%
                                        </text>
                                        <text x={55} y={66} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 10, fill: "#94a3b8" }}>
                                            attended
                                        </text>
                                    </svg>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", textAlign: "left" }}>
                                        {[
                                            ["Attended", overall.attended ?? 0, "#16a34a"],
                                            ["Absent", overall.absent ?? 0, "#dc2626"],
                                            ["Late", overall.late ?? 0, "#d97706"],
                                            ["Total", overall.totalLectures ?? 0, "#6b7280"],
                                        ].map(([l, v, c]) => (
                                            <div key={l} style={{ padding: "0.4rem 0.5rem", background: "#f8fafc", borderRadius: 6 }}>
                                                <div style={{ fontSize: 16, fontWeight: 700, color: c }}>{v}</div>
                                                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{l}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Lecture Log Table */}
                            <div className="card">
                                <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontWeight: 700 }}>
                                        <FiClock size={14} /> Lecture Attendance History
                                    </span>
                                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                        {subjectEntries.length} Recorded Sessions
                                    </span>
                                </div>

                                {subjectEntries.length === 0 ? (
                                    <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                                        No lecture sessions found for the selected month.
                                    </div>
                                ) : (
                                    <div className="table-wrapper">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Time / Period</th>
                                                    <th>Subject</th>
                                                    <th style={{ textAlign: "center" }}>Status</th>
                                                    <th>Remarks</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {subjectEntries.map((log, idx) => {
                                                    const sc = STATUS_COLORS[log.status] || STATUS_COLORS.Unmarked;
                                                    return (
                                                        <tr key={idx}>
                                                            <td style={{ fontWeight: 600, fontSize: 13 }}>{formatDisplayDate(log.attendanceDate)}</td>
                                                            <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                                Period #{log.periodNo} {log.startTime && log.endTime ? `(${log.startTime}–${log.endTime})` : ""}
                                                            </td>
                                                            <td style={{ fontWeight: 600, fontSize: 13 }}>
                                                                {log.subject?.name || "Subject"} {log.subject?.code && <span className="badge badge-blue">{log.subject.code}</span>}
                                                            </td>
                                                            <td style={{ textAlign: "center" }}>
                                                                <span
                                                                    style={{
                                                                        background: sc.bg,
                                                                        color: sc.color,
                                                                        border: `1px solid ${sc.border}`,
                                                                        padding: "2px 9px",
                                                                        borderRadius: 99,
                                                                        fontSize: 11.5,
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    {log.status}
                                                                </span>
                                                            </td>
                                                            <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                                {log.remarks || "—"}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════════════
                TAB 2: DAILY ROLL CALL
               ══════════════════════════════════════════════════════════════════════ */}
            {activeTab === "daily" && (
                <div>
                    {dailyPct > 0 && dailyPct < 75 && (
                        <div
                            style={{
                                background: "#fef3c7",
                                border: "1px solid #fcd34d",
                                borderRadius: 8,
                                padding: "0.75rem 1rem",
                                marginBottom: "1rem",
                                fontSize: 13,
                                color: "#92400e",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <FiAlertTriangle size={14} /> Your daily roll call attendance is <strong>{dailyPct}%</strong> — below the required 75%.
                        </div>
                    )}
                    {dailyPct >= 75 && (
                        <div
                            style={{
                                background: "#f0fdf4",
                                border: "1px solid #bbf7d0",
                                borderRadius: 8,
                                padding: "0.75rem 1rem",
                                marginBottom: "1rem",
                                fontSize: 13,
                                color: "#15803d",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <FiCheckCircle size={14} /> Good attendance! You are at <strong>{dailyPct}%</strong>.
                        </div>
                    )}

                    {dailyLoading ? (
                        <div className="loading-center">
                            <div className="spinner" /> Loading daily attendance…
                        </div>
                    ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 260px", gap: "1rem", alignItems: "start" }}>
                            {/* Calendar heatmap */}
                            <div className="card">
                                <div className="card-header">{MONTHS_LONG[month]} {year} — Day by Day</div>
                                <div style={{ padding: "1rem" }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "0.3rem", marginBottom: "0.3rem" }}>
                                        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                                            <div key={i} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", padding: "0.25rem 0" }}>
                                                {d}
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "0.3rem" }}>
                                        {cells.map((day, i) => {
                                            if (!day) return <div key={`b-${i}`} />;
                                            const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                                            const entry = dateMap[dateStr];
                                            const sc = entry ? STATUS_COLORS[entry.status] || { bg: "#f3f4f6", color: "#6b7280" } : null;
                                            const isToday = dateStr === todayStr;
                                            return (
                                                <div
                                                    key={day}
                                                    title={entry ? entry.status : ""}
                                                    style={{
                                                        aspectRatio: "1",
                                                        borderRadius: 6,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: 12,
                                                        fontWeight: isToday ? 800 : 500,
                                                        background: sc ? sc.bg : isToday ? "#e8f1f8" : "#f8fafc",
                                                        color: sc ? sc.color : isToday ? "#003b67" : "var(--text-muted)",
                                                        outline: isToday ? "2px solid #93c5fd" : "none",
                                                        outlineOffset: 1,
                                                    }}
                                                >
                                                    {day}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Legend */}
                                    <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "0.85rem" }}>
                                        {Object.entries(STATUS_COLORS).map(([label, sc]) => (
                                            <div key={label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11.5 }}>
                                                <div style={{ width: 10, height: 10, borderRadius: 3, background: sc.bg, border: `1px solid ${sc.color}40` }} />
                                                <span style={{ color: "var(--text-muted)" }}>{label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Summary */}
                            <div style={{ display: "grid", gap: "1rem" }}>
                                <div className="card" style={{ padding: "1.25rem", textAlign: "center" }}>
                                    <svg width={110} height={110} style={{ display: "block", margin: "0 auto 0.75rem" }}>
                                        <circle cx={55} cy={55} r={44} fill="none" stroke={`${dailyColor}18`} strokeWidth={10} />
                                        <circle
                                            cx={55}
                                            cy={55}
                                            r={44}
                                            fill="none"
                                            stroke={dailyColor}
                                            strokeWidth={10}
                                            strokeDasharray={`${2 * Math.PI * 44}`}
                                            strokeDashoffset={`${2 * Math.PI * 44 * (1 - dailyPct / 100)}`}
                                            strokeLinecap="round"
                                            transform="rotate(-90 55 55)"
                                        />
                                        <text x={55} y={50} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 20, fontWeight: 900, fill: dailyColor }}>
                                            {dailyPct}%
                                        </text>
                                        <text x={55} y={66} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 10, fill: "#94a3b8" }}>
                                            attendance
                                        </text>
                                    </svg>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", textAlign: "left" }}>
                                        {[
                                            ["Present", dailySummary.present ?? 0, "#16a34a"],
                                            ["Absent", dailySummary.absent ?? 0, "#dc2626"],
                                            ["Late", dailySummary.late ?? 0, "#d97706"],
                                            ["Leave", dailySummary.leave ?? 0, "#003b67"],
                                            ["Total", dailySummary.total ?? 0, "#6b7280"],
                                        ].map(([l, v, c]) => (
                                            <div key={l} style={{ padding: "0.4rem 0.5rem", background: "#f8fafc", borderRadius: 6 }}>
                                                <div style={{ fontSize: 16, fontWeight: 700, color: c }}>{v}</div>
                                                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{l}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {dailyEntries.length > 0 && (
                                    <div className="card">
                                        <div className="card-header" style={{ fontSize: 13 }}>Recent Records</div>
                                        <div>
                                            {[...dailyEntries]
                                                .reverse()
                                                .slice(0, 8)
                                                .map((entry) => {
                                                    const sc = STATUS_COLORS[entry.status] || { bg: "#f3f4f6", color: "#6b7280" };
                                                    return (
                                                        <div
                                                            key={entry.date}
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                padding: "0.5rem 1rem",
                                                                borderBottom: "1px solid var(--border)",
                                                                fontSize: 13,
                                                            }}
                                                        >
                                                            <span style={{ color: "var(--text-muted)" }}>{entry.date}</span>
                                                            <span style={{ ...sc, padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>
                                                                {entry.status}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}